/** @jsx jsx */
import React, { useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';
import {
  Box,
  Stepper,
  StepConnector,
  Step,
  StepLabel,
  Divider,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import FiberManualRecordOutlinedIcon from '@material-ui/icons/FiberManualRecordOutlined';
import { addDays, format } from 'date-fns/fp';
import { map, range } from 'ramda';

import useTranslate, {
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';
import styles from './styles';

import { SplitPayInitialOffer } from 'state/initialFinanceOffers';
import { conformToMask, decimalMoneyMask } from 'utils/masks';

import ExpandablePanel from 'components/ExpandablePanel';
import BreadTitle from './BreadTitle';
import useAnalytics, { Event } from 'hooks/useAnalytics';

interface SplitPayOfferData {
  id: string;
  maxCartSize: number;
  minCartSize: number;
  biweeklyPayment: number;
  termLength: number;
}

export interface SplitPayPaymentOptionProps {
  intendedPurchaseAmount: number;
  splitPayOffer: SplitPayInitialOffer;
  expanded: boolean;
  panelName: string;
  onChange: React.ComponentProps<typeof ExpandablePanel>['onChange'];
}

const getSplitPayOfferData = (
  principal: number,
  offer: SplitPayInitialOffer,
): SplitPayOfferData => {
  const termLength = offer.termLength;
  const biweeklyPayment = principal / termLength;
  return {
    id: offer.id,
    maxCartSize: offer.maxCartSize,
    minCartSize: offer.minCartSize,
    biweeklyPayment,
    termLength,
  };
};

const getDateLabelsFromToday = (period: number, numberOfPeriods: number) => {
  const today = new Date();
  const dateLabels = map(i => {
    const date = addDays(period * i, today);
    return {
      label: format('MMM dd, yyyy', date),
    };
  }, range(1, numberOfPeriods));

  return [
    {
      label: 'Today',
    },
    ...dateLabels,
  ];
};

const StepperIcon: React.FC<{ active: boolean }> = props => {
  return props.active ? (
    <FiberManualRecordIcon />
  ) : (
    <FiberManualRecordOutlinedIcon />
  );
};

const SplitPay: React.FC<SplitPayPaymentOptionProps> = props => {
  const {
    intendedPurchaseAmount,
    panelName,
    splitPayOffer,
    expanded,
    onChange,
  } = props;

  const splitPayOfferData = useMemo(
    () => getSplitPayOfferData(intendedPurchaseAmount, splitPayOffer),
    [intendedPurchaseAmount, splitPayOffer],
  );

  const { sendEvents } = useAnalytics();

  useEffect(() => {
    expanded && sendEvents([Event.SplitpayInitialOfferExpanded]);
  }, [expanded, sendEvents]);

  const steps = useMemo(
    () =>
      getDateLabelsFromToday(
        14, // BIWEEKLY
        splitPayOffer.termLength,
      ),
    [splitPayOffer.termLength],
  );

  const t = useTranslate();
  const tMd = useMarkdownTranslate();

  const inErrorOver = useMemo(() => {
    return intendedPurchaseAmount > splitPayOfferData.maxCartSize / 100;
  }, [intendedPurchaseAmount, splitPayOfferData.maxCartSize]);

  const inErrorUnder = useMemo(() => {
    return intendedPurchaseAmount < splitPayOfferData.minCartSize / 100;
  }, [intendedPurchaseAmount, splitPayOfferData.minCartSize]);

  const capacityValueExceeded = useMemo(() => {
    return conformToMask(
      (intendedPurchaseAmount - splitPayOfferData.maxCartSize / 100).toFixed(2),
      decimalMoneyMask,
    );
  }, [intendedPurchaseAmount, splitPayOfferData.maxCartSize]);

  const capacityValueBeneath = useMemo(() => {
    return conformToMask(
      (splitPayOfferData.minCartSize / 100 - intendedPurchaseAmount).toFixed(2),
      decimalMoneyMask,
    );
  }, [intendedPurchaseAmount, splitPayOfferData.minCartSize]);

  const bannerSecondaryText = useMemo(() => {
    if (inErrorOver) {
      return tMd('unified.offer.intendedPurchaseAmountMaximumError', {
        amount: capacityValueExceeded,
      });
    }
    if (inErrorUnder) {
      return tMd('unified.offer.intendedPurchaseAmountMinimumError', {
        amount: capacityValueBeneath,
      });
    }
    return null;
  }, [
    capacityValueBeneath,
    capacityValueExceeded,
    inErrorOver,
    inErrorUnder,
    tMd,
  ]);

  const handlePanelExpandChangeWhenNotError = useCallback(
    (event: React.ChangeEvent<{}>, panelName: string, expanded: boolean) => {
      if (!inErrorOver && !inErrorUnder) {
        if (onChange) {
          onChange(event, panelName, expanded);
        }
      }
    },
    [inErrorOver, inErrorUnder, onChange],
  );

  const title = (
    <div css={styles.paymentOptionTitleText}>
      <BreadTitle
        titlePart={tMd('unified.offerIntro.paymentOption.splitPay.title')}
        logo={t('unified.offerIntro.paymentOption.splitPay.breadLogo')}
        id={'splitpayBreadLogo'}
      />
      {inErrorOver || inErrorUnder ? null : (
        <div css={styles.paymentOptionText}>
          {tMd('unified.offerIntro.paymentOption.splitPay.description', {
            amount: conformToMask(
              splitPayOfferData.biweeklyPayment.toFixed(2),
              decimalMoneyMask,
            ),
            termLength: splitPayOfferData.termLength,
          })}
        </div>
      )}
    </div>
  );

  const stepConnector = <StepConnector css={styles.stepConnector} />;
  const body = (
    <Box mb={2} css={styles.paymentOptionText}>
      <div css={styles.divider}>
        <Divider />
      </div>
      <div css={styles.splitPayPaymentsMessage}>
        {tMd('unified.offerIntro.paymentOption.splitPay.paymentsMessage')}
      </div>

      <Box css={styles.stepperContainer}>
        <Stepper
          activeStep={0}
          orientation="vertical"
          connector={stepConnector}
          css={styles.stepper}
        >
          {steps.map(step => (
            <Step key={step.label}>
              <StepLabel
                css={styles.stepCircle}
                StepIconComponent={StepperIcon}
              >
                {step.label}
                <span css={{ float: 'right' }}>
                  <strong css={styles.stepPrice}>
                    {conformToMask(
                      splitPayOfferData.biweeklyPayment.toFixed(2),
                      decimalMoneyMask,
                    )}
                  </strong>
                </span>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Box>
  );
  return (
    <ExpandablePanel
      expanded={inErrorOver || inErrorUnder ? false : expanded}
      hideToggleIcon={inErrorOver || inErrorUnder}
      title={title}
      children={body}
      panelName={panelName}
      bannerSecondaryText={bannerSecondaryText}
      onChange={handlePanelExpandChangeWhenNotError}
    />
  );
};

export default SplitPay;
